import React, { useState } from "react";
import DOMPurify from "dompurify";
import { useTheme } from "@mui/material/styles";

import {
  Toolbar,
  Grid,
  Avatar,
  Typography,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  IconButton,
  Badge,
  ListItemText,
  Tooltip,
  Button,
} from "@mui/material";
import { AccountCircle, ExitToApp } from "@mui/icons-material";
import NavigationBarStyles from "./NavigationBar.Styles";
import { useNavigate } from "react-router-dom";
import useLogout from "@Hooks/useLogout";
import { styled } from "@mui/system";
import LogoTitle from "@Components/LogoTitle/LogoTitle";

import NotificationsIcon from "@mui/icons-material/Notifications";
import { useEffect } from "react";
import useGetAll from "@Hooks/useGetAll";
import { NOTIFICATIONS_URL, GET_FIRST_TEN_NOTIFICATIONS, MARK_ALL_NOTIFICATIONS_READ, GET_UNREAD_COUNT } from "@API/APIRoutes";
import usePostItem from "@Hooks/usePostItem";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import { formatNotifications } from "@Utils/Validations/HelperFunctions/FormaterFunctions";
import { useSelector } from "react-redux";
import {
  getDummyNotifications,
  formatNotificationDate,
} from "@Utils/DummyData/NotificationsDummyData";

const UserMenu = styled(Menu)(({ theme }) => ({
  "& .menuPaper": {
    width: theme.spacing(18),
    boxShadow: theme.shadows.main,
  },
  "& .menuList": {
    padding: 0,
  },
}));

const NotificationMenuItem = styled(MenuItem)(({ theme, isUnread }) => ({
  borderBottom: "1px solid #e0e0e0",
  backgroundColor: isUnread ? "#f0f7ff" : "inherit",
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: isUnread ? "#e6f2ff" : "#f5f5f5",
  },
}));

const NavigationBar = ({ text, user, isDisabled }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [anchor, setAnchor] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const { loading, getAll } = useGetAll();
  const { loading: postLoading, postItem } = usePostItem();

  useEffect(() => {
    if (user.roles !== "ADMIN") {
      // Fetch first 10 notifications for display
      getAll(GET_FIRST_TEN_NOTIFICATIONS).then((response) => {
        console.log("Fetched notifications:", response);
        const formattedResponse = formatNotifications(response);
        setNotifications(formattedResponse);
      });
      
      // Fetch total unread count
      getAll(GET_UNREAD_COUNT).then((count) => {
        setTotalUnreadCount(count || 0);
      });
    }
  }, []);

  const toggleAnchor = (event) => {
    if (isDisabled) {
      return;
    }
    if (anchor) {
      setAnchor(null);
    } else {
      setAnchor(event.currentTarget);
    }
  };

  const toggleNotificationMenu = (event) => {
    if (notificationAnchor) {
      setNotificationAnchor(null);
    } else {
      setNotificationAnchor(event.currentTarget);
    }
  };

  const handleNotificationClick = async (notification) => {
    setNotificationAnchor(null);
    
    // Only decrement if notification is unread
    const wasUnread = !notification.read;
    
    postItem(
      `${NOTIFICATIONS_URL}/${notification.id}/read`,
      {},
      () => {},
      "",
      "Failed to mark notification as read",
      false
    );
    // Mark notification as read in our state
    const updatedNotifications = notifications.map((n) =>
      n.id === notification.id ? { ...n, read: true } : n
    );
    setNotifications(updatedNotifications);
    
    // Decrement total count if it was unread
    if (wasUnread && totalUnreadCount > 0) {
      setTotalUnreadCount(totalUnreadCount - 1);
    }

    if (
      notification.subject === "DELEGATION REQUEST" ||
      notification.subject === "DELEGATION ACCEPTED" ||
      notification.subject === "DELEGATION REJECTED"
    ) {
      user.role === "REGULATORY"
        ? navigate(DefaultRoutes.regDelegationRequests)
        : navigate(DefaultRoutes.rdDelegationRequests);
    } else if (
      notification.subject === "SUPPORT REQUEST CREATED" ||
      (notification.supportRequestId && !notification.exitRequestId)
    ) {
      // Navigate to support request detail page
      if (notification.supportRequestId) {
        navigate(
          DefaultRoutes.supportRequestDetail.replace(
            ":requestId",
            notification.supportRequestId
          )
        );
      } else {
        // Fallback: navigate to support provider home if no ID available
        navigate(DefaultRoutes.supportProviderHome);
      }
    } else if (notification.exitRequestId) {
      // For exit request notifications, navigate to exit request info
      navigate(
        DefaultRoutes.reqInfo.replace(":reqId", notification.exitRequestId)
      );
    } else {
      // Fallback: no valid ID found
      console.warn('Notification has no valid route ID:', notification);
    }

  };

  const viewAllNotifications = () => {
    setNotificationAnchor(null);
    navigate(DefaultRoutes.notifications);
  };

  const handleMarkAllAsRead = () => {
    postItem(
      MARK_ALL_NOTIFICATIONS_READ,
      {},
      () => {
        const updatedNotifications = notifications.map((n) => ({
          ...n,
          read: true,
        }));
        setNotifications(updatedNotifications);
        setTotalUnreadCount(0);
      },
      "",
      "Failed to mark all notifications as read",
      false
    );
  };

  const getUnreadCount = () => {
    return totalUnreadCount;
  };

  const logout = useLogout();

  // Check if notifications should be shown (only for REGULATORY and R&D roles)
  const showNotifications = user.role && user.role !== "ADMIN";

  return (
    <NavigationBarStyles isDisabled={isDisabled}>
      <Box className="appBar">
        <Toolbar className="logoToolbar">
          <Grid container alignItems="center" justifyContent="space-between">
            <LogoTitle isDisabled={isDisabled} />
            <Box display="flex" alignItems="center">
              {showNotifications && (
                <IconButton onClick={toggleNotificationMenu} color="inherit">
                  <Badge badgeContent={getUnreadCount()} color="error">
                    <NotificationsIcon color={"primary"} />
                  </Badge>
                </IconButton>
              )}
              {/* Notification Menu */}
              {showNotifications && (
                <UserMenu
                  anchorEl={notificationAnchor}
                  open={Boolean(notificationAnchor)}
                  onClose={() => setNotificationAnchor(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    sx: {
                      width: 360,
                      maxHeight: 400,
                    },
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      backgroundColor: "#f5f5f5",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium">
                      Notifications
                    </Typography>
                  </Box>
                  {notifications.length === 0 ? (
                    <MenuItem
                      sx={{
                        justifyContent: "center",
                        backgroundColor: "#fafafa",
                        py: 3,
                      }}
                    >
                      <ListItemText
                        primary="No Notifications"
                        primaryTypographyProps={{
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      />
                    </MenuItem>
                  ) : (
                    <>
                      {notifications.slice(0, 5).map((notification) => (
                        <NotificationMenuItem
                          key={notification.id}
                          isUnread={!notification.read}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <Box sx={{ width: "100%" }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{
                                  color: notification.read
                                    ? "text.primary"
                                    : "primary.main",
                                }}
                              >
                                {notification.subject}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatNotificationDate(notification.createdAt)}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              component="div"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                "& a": {
                                  color: theme.palette.primary.main,
                                  textDecoration: "underline",
                                },
                              }}
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(notification.message, {
                                  ALLOWED_TAGS: ["br", "a", "strong", "em", "p"],
                                  ALLOWED_ATTR: ["href", "target", "rel"],
                                }),
                              }}
                            />
                          </Box>
                        </NotificationMenuItem>
                      ))}
                      {notifications.length > 0 && (
                        <MenuItem
                          onClick={handleMarkAllAsRead}
                          disabled={notifications.filter((n) => !n.read).length === 0}
                          sx={{
                            justifyContent: "center",
                            backgroundColor: "#f9f9f9",
                            py: 1,
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          <ListItemText
                            primary="Mark All as Read"
                            primaryTypographyProps={{
                              fontWeight: "medium",
                              textAlign: "center",
                              color: notifications.filter((n) => !n.read).length === 0 ? "text.disabled" : "primary.main",
                            }}
                          />
                        </MenuItem>
                      )}
                      <MenuItem
                        onClick={viewAllNotifications}
                        sx={{
                          justifyContent: "center",
                          backgroundColor: "#f5f5f5",
                          py: 1,
                        }}
                      >
                        <ListItemText
                          primary="View All Notifications"
                          primaryTypographyProps={{
                            color: "primary",
                            textAlign: "center",
                            fontWeight: "medium",
                            fontSize: "0.875rem",
                          }}
                        />
                      </MenuItem>
                    </>
                  )}
                </UserMenu>
              )}
              <Box className="userSection" onClick={toggleAnchor}>
                <Avatar src="/profileAvatarLight.jpg" className="avatar" />
                <Typography className="userName" variant="body1">
                  {user.username}
                </Typography>
                <UserMenu
                  anchorEl={anchor}
                  open={Boolean(anchor)}
                  onClose={() => setAnchor(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PopoverClasses={{
                    root: "popoverRootOverride",
                    paper: "menuPaper",
                  }}
                  MenuListProps={{
                    className: "menuList",
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setAnchor(null);
                      navigate("/profile");
                    }}
                  >
                    <ListItemIcon>
                      <AccountCircle fontSize="small" />
                    </ListItemIcon>
                    Profile
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      toggleAnchor();
                      logout();
                    }}
                  >
                    <ListItemIcon>
                      <ExitToApp fontSize="small" color={"error"} />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </UserMenu>
              </Box>
            </Box>
          </Grid>
        </Toolbar>
      </Box>
    </NavigationBarStyles>
  );
};

export default NavigationBar;
